import React, { useState, useEffect, useCallback, startTransition } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DesktopView from "../layout/DesktopView";
import SkillCard from "./SkillCard";
import SkillEditor from "./SkillEditor";
import { useToast } from "../common/ToastContext";
import { skillService } from "../../services/skillService";
import { useSkillsContext } from "../../hooks/useSkillsContext";

const SkillDesktop = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [goBack, setGoBack] = useState(null);
  const { showToast } = useToast();
  const {
    fetchSkills,
    skills,
    selectedSkill,
    selectedCoreSkillKey,
    setSelectedSkill,
  } = useSkillsContext();

  useEffect(() => {
    if (searchParams.has("id") && !selectedSkill) {
      const editSkillId = searchParams.get("id");
      const editSkill = skills.find((skill) => skill.id === editSkillId);
      if (editSkill) {
        setSelectedSkill(editSkill);
        if (searchParams.has("goback")) {
          setGoBack(searchParams.get("goback"));
          setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete("goback");
            return newParams;
          });
        }
      }
    }
  }, [searchParams, skills, selectedSkill, setSearchParams]);

  const handleSaveSkill = async (skillData) => {
    if (!skillData.isCore && !skillData.overloads) {
      showToast("Please select a core skill first", "error");
      return;
    }

    try {
      if (skillData.id && !skillData.id.startsWith("core-")) {
        await skillService.update(skillData);
      } else {
        const { id, ...newSkillData } = skillData;
        await skillService.create(newSkillData);
      }
      await fetchSkills();
      handleCloseSkillEditor();
      showToast("Skill saved successfully", "info");
    } catch (error) {
      console.error("Error saving skill:", error);
      showToast(`Error saving skill: ${error}`, "error");
    }
  };

  const handleDeleteSkill = async (skillId) => {
    try {
      await skillService.delete(skillId);
      await fetchSkills();
      handleCloseSkillEditor();
      showToast("Skill deleted successfully", "info");
    } catch (error) {
      console.error("Error deleting skill:", error);
      showToast(`Error deleting skill: ${error}`, "error");
    }
  };

  const handleResetSkill = async (skillId) => {
    if (skillId.startsWith("core-")) return;
    handleDeleteSkill(skillId);
  };

  const handleCloseSkillEditor = () => {
    // Must commit with React Router v7's transitioned URL update, or the id effect reopens the editor.
    startTransition(() => setSelectedSkill(null));
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete("id");
      return newParams;
    });
    if (goBack) {
      const goto = goBack;
      setGoBack(null);
      navigate(goto + "?reload=true");
    }
  };

  const handleAddNewSkill = async () => {
    const randFourDigits = Math.floor(1000 + Math.random() * 9000);
    const newSkillName = `New Skill ${randFourDigits}`;
    const newSkill = {
      key: newSkillName,
      description: "",
      tags: [],
      preProcessing: false,
      postProcessing: false,
      example: "",
      overloads: selectedCoreSkillKey,
    };

    try {
      const createdSkill = await skillService.create(newSkill);
      await fetchSkills();
      setSelectedSkill({ ...createdSkill, isCore: false });
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set("id", createdSkill.id);
        return newParams;
      });
      showToast("New skill created successfully", "info");
    } catch (error) {
      console.error("Error creating new skill:", error);
      showToast(`Error creating new skill: ${error}`, "error");
    }
  };

  const handleSkillClick = (skill) => {
    setSelectedSkill(skill);
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set("id", skill.id);
      return newParams;
    });
  };

  const handleSearch = (term) => {
    // This function is left empty as search is handled internally by DesktopView
  };

  return (
    <DesktopView
      items={skills}
      selectedItem={selectedSkill}
      onSearch={handleSearch}
      searchPlaceholder="Filter skills ..."
      contextMenuItems={[]}
      onItemClick={handleSkillClick}
      onSave={handleSaveSkill}
      onDelete={handleDeleteSkill}
      onReset={handleResetSkill}
      onClose={handleCloseSkillEditor}
      Card={SkillCard}
      Editor={SkillEditor}
      addNewItem={handleAddNewSkill}
      eventName="skillCreated"
    />
  );
};

export default SkillDesktop;
